import * as Sentry from '@sentry/vue'
import type { AxiosError } from 'axios'
import { ref } from 'vue'

import { handleBlobResponseErrorMessage } from '@/lib'
import { request } from '@/lib/services'
import { useErrorStore } from '@/stores/error.stores'

type DownloadOptions = {
  silent?: boolean
}

const safeUrlOrigin = (url: string): string | undefined => {
  try {
    const parsed = new URL(url)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return undefined
  }
}

const classifyNetworkError = (error: AxiosError, durationMs: number): string => {
  if (error.response) {
    return 'http-error'
  }
  if (error.code === 'ECONNABORTED') {
    return 'timeout'
  }
  if (!navigator.onLine) {
    return 'offline'
  }
  return durationMs < 300 ? 'likely-cors-or-dns' : 'likely-connection-drop'
}

export const useFileDownload = (requestService = request) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const downloadFile = async (
    url: string,
    filename?: string,
    options?: DownloadOptions,
  ): Promise<void> => {
    const startedAt = performance.now()
    try {
      isLoading.value = true
      error.value = null

      const response = await requestService.getBlob(url)

      const blob = response.data
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl

      if (!filename) {
        const timestamp = new Date().toISOString().split('T')[0]
        filename = `download-${timestamp}.xlsx`
      }

      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (e: unknown) {
      const axiosError = e as AxiosError
      const durationMs = Math.round(performance.now() - startedAt)

      Sentry.captureException(e, {
        tags: {
          feature: 'file-download',
          'file-download.likely-cause': classifyNetworkError(axiosError, durationMs),
        },
        extra: {
          filename,
          urlOrigin: safeUrlOrigin(url),
          durationMs,
          isOnline: navigator.onLine,
          axiosCode: axiosError.code,
          hasResponse: Boolean(axiosError.response),
        },
      })

      if (!options?.silent) {
        errorStore.setError(await handleBlobResponseErrorMessage(e))
      }
      error.value = e as Error
      throw e
    } finally {
      isLoading.value = false
    }
  }

  return {
    downloadFile,
    isLoading,
    error,
  }
}
